import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { sessions, agents } from '../api';
import {
  Download, Phone, PhoneIncoming, PhoneOutgoing, Globe, Clock, RefreshCw,
  Search, ChevronDown, ChevronLeft, ChevronRight, Calendar, Bot,
  FileSpreadsheet, X, Check, Gauge, Eye, EyeOff, Columns3, FileText,
} from 'lucide-react';
import Toast from './Toast';
import LoadingSpinner from './LoadingSpinner';
import VisitorBadge, { visitorExportText } from './VisitorBadge';
import AnimatedDropdown from './AnimatedDropdown';

// ─── Themed Calendar Picker ───────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const ThemedDatePicker = ({ value, onChange, label, icon: Icon, maxDate, minDate }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selected ? selected.getMonth() : new Date().getMonth());

  useEffect(() => {
    if (open && selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const isDisabled = (d) => {
    const ds = toDateStr(d);
    if (maxDate && ds > maxDate) return true;
    if (minDate && ds < minDate) return true;
    return false;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevDays = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, outside: true, date: new Date(viewYear, viewMonth - 1, prevDays - i) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, outside: false, date: new Date(viewYear, viewMonth, d) });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, outside: true, date: new Date(viewYear, viewMonth + 1, d) });
  }

  const today = new Date();
  const todayStr = toDateStr(today);

  const displayValue = selected
    ? selected.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const panelPosition = () => {
    if (!btnRef.current) return {};
    const r = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    if (below < 360) {
      return { position: 'fixed', bottom: window.innerHeight - r.top + 8, left: r.left, width: Math.max(r.width, 310), zIndex: 99999 };
    }
    return { position: 'fixed', top: r.bottom + 8, left: r.left, width: Math.max(r.width, 310), zIndex: 99999 };
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {Icon && <Icon className="w-4 h-4 inline mr-1.5" />}
        {label}
      </label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full bg-background border ${
          open ? 'border-primary shadow-lg shadow-primary/20' : 'border-primary/50 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
        } rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 flex items-center justify-between cursor-pointer`}
      >
        <span className={`flex items-center gap-2 ${displayValue ? 'text-foreground' : 'text-muted-foreground'}`}>
          <Calendar className="w-4 h-4 flex-shrink-0 text-primary" />
          {displayValue || 'Select date'}
        </span>
        <ChevronDown className={`w-4 h-4 text-primary transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={panelPosition()}
          className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl shadow-elegant overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
        >
          {/* Month / Year header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-foreground select-none">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day-of-week labels */}
          <div className="grid grid-cols-7 px-3 pt-3 pb-1">
            {DAY_LABELS.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider py-1">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 px-3 pb-3 gap-y-0.5">
            {cells.map((c, i) => {
              const ds = toDateStr(c.date);
              const isSel = value === ds;
              const isToday = ds === todayStr;
              const disabled = isDisabled(c.date);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled || c.outside}
                  onClick={() => { onChange(ds); setOpen(false); }}
                  className={`
                    relative w-full aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150
                    ${c.outside ? 'text-muted-foreground/30 cursor-default' : ''}
                    ${disabled && !c.outside ? 'text-muted-foreground/40 cursor-not-allowed' : ''}
                    ${!c.outside && !disabled && !isSel ? 'hover:bg-primary/10 hover:text-primary cursor-pointer text-foreground' : ''}
                    ${isSel ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30 hover:bg-primary/90' : ''}
                    ${isToday && !isSel ? 'ring-1 ring-primary/50' : ''}
                  `}
                >
                  {c.day}
                  {isToday && !isSel && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 px-3 pb-3">
            <button
              type="button"
              onClick={() => { onChange(todayStr); setOpen(false); }}
              className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              Today
            </button>
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border/60 text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Column Visibility Dropdown ───────────────────────────────────────────────

const ColumnVisibilityDropdown = ({ columns, visible, onToggle, onShowAll, onHideAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const visibleCount = columns.filter((c) => visible[c.key]).length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="px-4 py-2 bg-card border border-border hover:border-primary/50 text-foreground rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium hover:shadow-md"
      >
        <Columns3 className="w-4 h-4 text-primary" />
        Columns
        <span className="text-xs text-muted-foreground">({visibleCount})</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-elegant z-50 overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-primary" />
                Visible Columns
              </span>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2">
              <button onClick={onShowAll} className="text-xs text-primary hover:underline">Show All</button>
              <span className="text-xs text-muted-foreground">|</span>
              <button onClick={onHideAll} className="text-xs text-primary hover:underline">Hide All</button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
            {columns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onToggle(col.key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                  visible[col.key] ? 'bg-primary border-primary' : 'border-border bg-background'
                }`}>
                  {visible[col.key] && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-foreground">{col.label}</span>
                {col.isExtraction && <span className="text-[10px] text-primary/60 ml-auto bg-primary/10 px-1.5 py-0.5 rounded">extracted</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CallHistoryExport = () => {
  const [agentsList, setAgentsList] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sessionsList, setSessionsList] = useState([]);
  const [extractionFields, setExtractionFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sessionsPerPage] = useState(20);
  const [toast, setToast] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState({});
  const exportRef = useRef(null);

  const [visibleColumns, setVisibleColumns] = useState({});
  const initialLoadDone = useRef(false);

  const baseColumns = [
    { key: 'call_direction', label: 'Direction' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'channel', label: 'Source' },
    { key: 'visitor', label: 'Visitor' },
    { key: 'agent_name', label: 'Agent' },
    { key: 'duration', label: 'Duration' },
    { key: 'avg_response_time_ms', label: 'Latency' },
    { key: 'status', label: 'Status' },
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'summary', label: 'Summary' },
    { key: 'started_at', label: 'Start Time' },
    { key: 'ended_at', label: 'End Time' },
    { key: 'cost_usd', label: 'Cost (USD)' },
  ];

  const allTableColumns = [
    ...baseColumns,
    ...extractionFields.map((f) => ({ key: `ext_${f.name}`, label: f.name, isExtraction: true })),
  ];

  const allExportColumns = [
    ...baseColumns,
    ...extractionFields.map((f) => ({ key: `ext_${f.name}`, label: f.name })),
  ];

  useEffect(() => {
    loadAgents();

    const defaults = {};
    baseColumns.forEach((c) => { defaults[c.key] = true; });
    setVisibleColumns(defaults);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    if (exportOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportOpen]);

  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    return { start: fmt(start), end: fmt(end) };
  };

  const handleAgentChange = useCallback((agentId) => {
    setSelectedAgentId(agentId);
    if (!agentId) return;

    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    if (selectedAgentId && startDate && endDate && !initialLoadDone.current) {
      initialLoadDone.current = true;
      doLoadCallHistory(selectedAgentId, startDate, endDate, 1);
    }
  }, [selectedAgentId, startDate, endDate]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await agents.list(false);
      const list = response.data || [];
      setAgentsList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setToast({ message: 'Failed to load agents', type: 'error' });
    } finally {
      setLoadingAgents(false);
    }
  };

  const doLoadCallHistory = async (agentId, sd, ed, page) => {
    if (!agentId || !sd || !ed) return;
    try {
      setLoading(true);
      const response = await sessions.callHistory(agentId, sd, ed, page, sessionsPerPage);
      const data = response.data;
      setSessionsList(data.sessions || []);
      setExtractionFields(data.extraction_fields || []);
      setTotalSessions(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setCurrentPage(data.page || 1);
      setHasFetched(true);

      const expDefaults = {};
      baseColumns.forEach((c) => { expDefaults[c.key] = true; });
      (data.extraction_fields || []).forEach((f) => { expDefaults[`ext_${f.name}`] = true; });
      setSelectedExportColumns(expDefaults);

      setVisibleColumns((prev) => {
        const next = { ...prev };
        (data.extraction_fields || []).forEach((f) => {
          const k = `ext_${f.name}`;
          if (next[k] === undefined) next[k] = true;
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to load call history:', error);
      setToast({ message: 'Failed to load call history', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCallHistory = (page = 1) => {
    doLoadCallHistory(selectedAgentId, startDate, endDate, page);
  };

  const handleSearch = () => {
    if (!selectedAgentId || !startDate || !endDate) {
      setToast({ message: 'Please select an agent and date range', type: 'error' });
      return;
    }
    initialLoadDone.current = true;
    setCurrentPage(1);
    loadCallHistory(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadCallHistory(page);
  };

  // Column visibility helpers
  const toggleVisibleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const showAllColumns = () => {
    const v = {};
    allTableColumns.forEach((c) => { v[c.key] = true; });
    setVisibleColumns(v);
  };
  const hideAllColumns = () => setVisibleColumns({});

  // Export helpers
  const toggleExportColumn = (key) => setSelectedExportColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectAllExportCols = () => {
    const all = {};
    allExportColumns.forEach((c) => { all[c.key] = true; });
    setSelectedExportColumns(all);
  };
  const deselectAllExportCols = () => setSelectedExportColumns({});

  const exportToCSV = async () => {
    if (!selectedAgentId || !startDate || !endDate) return;
    const chosen = allExportColumns.filter((c) => selectedExportColumns[c.key]);
    if (chosen.length === 0) {
      setToast({ message: 'Please select at least one column to export', type: 'error' });
      return;
    }
    try {
      setToast({ message: 'Preparing export...', type: 'info' });
      let allSessions = [];
      let page = 1;
      const batchSize = 100;
      let hasMore = true;
      while (hasMore) {
        const response = await sessions.callHistory(selectedAgentId, startDate, endDate, page, batchSize);
        const data = response.data;
        allSessions = allSessions.concat(data.sessions || []);
        hasMore = page < (data.total_pages || 0);
        page++;
      }
      const escapeCSV = (val) => {
        if (val == null) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };
      const header = chosen.map((c) => escapeCSV(c.label)).join(',');
      const rows = allSessions.map((s) => chosen.map((c) => escapeCSV(getExportValue(s, c.key))).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const agentName = agentsList.find((a) => a.id === selectedAgentId)?.name || 'agent';
      link.href = url;
      link.download = `call-history_${agentName}_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast({ message: `Exported ${allSessions.length} records successfully`, type: 'success' });
      setExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      setToast({ message: 'Export failed. Please try again.', type: 'error' });
    }
  };

  const getExportValue = (session, key) => {
    if (key.startsWith('ext_')) {
      const fieldName = key.slice(4);
      return session.extracted_data?.[fieldName] ?? '-';
    }
    switch (key) {
      case 'visitor': return visitorExportText(session.visitor);
      case 'call_direction': return session.call_direction || 'inbound';
      case 'phone_number': return session.caller_number || session.called_number || '-';
      case 'channel': return session.channel || '-';
      case 'agent_name': return session.agent_name || 'Unknown';
      case 'duration': return session.duration != null ? formatDuration(session.duration) : 'N/A';
      case 'avg_response_time_ms':
        return session.avg_response_time_ms != null
          ? `${Math.round(session.avg_response_time_ms)}ms (${(session.avg_response_time_ms / 1000).toFixed(2)}s)`
          : '-';
      case 'status': return session.status || '-';
      case 'sentiment': return session.sentiment || 'Neutral';
      case 'summary': return session.summary || '-';
      case 'started_at': return session.started_at ? new Date(session.started_at).toLocaleString() : '-';
      case 'ended_at': return session.ended_at ? new Date(session.ended_at).toLocaleString() : '-';
      case 'cost_usd': return session.cost_usd != null ? `$${session.cost_usd.toFixed(4)}` : '-';
      default: return '-';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '-';
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
      if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-primary/20 text-primary border-primary/30';
      case 'ended': case 'completed': return 'bg-success/20 text-success border-success/30';
      case 'failed': case 'error': case 'terminated': return 'bg-destructive/20 text-destructive border-destructive/30';
      case 'abandoned': return 'bg-warning/20 text-warning border-warning/30';
      case 'voicemail_reached': return 'bg-blue-500/20 text-blue-600 border-blue-500/30 dark:text-blue-400';
      default: return 'bg-muted/20 text-muted-foreground border-border';
    }
  };

  const getSentimentColor = (sentiment) => {
    if (!sentiment) return 'text-muted-foreground';
    switch (sentiment.toLowerCase()) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-destructive';
      default: return 'text-foreground';
    }
  };

  const agentOptions = agentsList.map((a) => ({ value: a.id, label: a.name }));
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  // Which base columns are currently visible
  const visibleBaseColumns = baseColumns.filter((c) => visibleColumns[c.key]);
  const visibleExtractionFields = extractionFields.filter((f) => visibleColumns[`ext_${f.name}`]);

  // ─── Render cell for a given base column key ──────────────────────────────
  const renderCell = (session, colKey) => {
    const phoneNumber = session.caller_number || session.called_number;
    switch (colKey) {
      case 'visitor':
        return <VisitorBadge visitor={session.visitor} />;
      case 'call_direction':
        return (
          <div className="flex items-center gap-2">
            <span className={session.call_direction === 'inbound' ? 'text-success' : 'text-primary'}>
              {session.call_direction === 'inbound' ? <PhoneIncoming className="w-4 h-4" /> : <PhoneOutgoing className="w-4 h-4" />}
            </span>
            <span className="text-sm font-medium text-foreground capitalize whitespace-nowrap">{session.call_direction || 'inbound'}</span>
          </div>
        );
      case 'phone_number':
        return <span className="text-sm text-foreground truncate block font-mono max-w-[140px]">{session.channel === 'phone' ? formatPhoneNumber(phoneNumber) : '-'}</span>;
      case 'channel':
        return (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0">
              {session.channel === 'phone' ? <Phone className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
            </span>
            <span className="text-sm text-foreground capitalize font-medium whitespace-nowrap">{session.channel}</span>
          </div>
        );
      case 'agent_name':
        return <span className="text-sm text-foreground truncate block font-medium max-w-[120px]">{session.agent_name || 'Unknown'}</span>;
      case 'duration':
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            <span className="text-sm text-foreground font-medium">{formatDuration(session.duration)}</span>
          </div>
        );
      case 'avg_response_time_ms':
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <Gauge className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            {session.avg_response_time_ms != null ? (
              <span className={`text-sm font-medium ${Math.round(session.avg_response_time_ms) <= 1400 ? 'text-success' : 'text-warning'}`}>
                {Math.round(session.avg_response_time_ms)}ms ({(session.avg_response_time_ms / 1000).toFixed(2)}s)
              </span>
            ) : (
              <span className="text-sm font-medium text-muted-foreground">-</span>
            )}
          </div>
        );
      case 'status':
        return (
          <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border whitespace-nowrap shadow-sm ${getStatusColor(session.status)}`}>
            {session.status === 'VOICEMAIL_REACHED' ? 'Voicemail Reached' : (session.status || '').toLowerCase()}
          </span>
        );
      case 'sentiment':
        return (
          <span className={`text-sm font-semibold capitalize px-2 py-1 rounded whitespace-nowrap ${getSentimentColor(session.sentiment)}`}>
            {session.sentiment || 'Neutral'}
          </span>
        );
      case 'summary':
        return (
          <div className="max-w-[300px] min-w-[180px]">
            {session.summary ? (
              <p className="text-sm text-foreground line-clamp-3 leading-relaxed" title={session.summary}>
                {session.summary}
              </p>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </div>
        );
      case 'started_at':
        return (
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <span className="text-sm text-foreground font-medium whitespace-nowrap">{formatDate(session.started_at)}</span>
            {session.ended_at && <span className="text-xs text-muted-foreground whitespace-nowrap">Ended: {formatDate(session.ended_at)}</span>}
          </div>
        );
      case 'ended_at':
        return <span className="text-sm text-foreground font-medium whitespace-nowrap">{session.ended_at ? formatDate(session.ended_at) : '-'}</span>;
      case 'cost_usd':
        return session.cost_usd != null
          ? <span className="text-sm text-success font-semibold">${session.cost_usd.toFixed(4)}</span>
          : <span className="text-sm text-muted-foreground">-</span>;
      default:
        return <span className="text-sm text-muted-foreground">-</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground mb-1">Call History & Export</h2>
        <p className="text-sm text-muted-foreground">View call history with extracted data and export to CSV.</p>
      </div>

      {/* Filters */}
      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-5 shadow-lg shadow-primary/5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Agent Selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Bot className="w-4 h-4 inline mr-1.5" />
              Agent
            </label>
            {loadingAgents ? (
              <div className="flex items-center justify-center h-[46px] bg-background border border-border/50 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            ) : (
              <AnimatedDropdown
                value={selectedAgentId}
                onChange={handleAgentChange}
                options={agentOptions}
                placeholder="Select an agent"
                icon={Bot}
              />
            )}
          </div>

          {/* Start Date */}
          <ThemedDatePicker
            value={startDate}
            onChange={setStartDate}
            label="Start Date"
            icon={Calendar}
            maxDate={endDate || todayStr}
          />

          {/* End Date */}
          <ThemedDatePicker
            value={endDate}
            onChange={setEndDate}
            label="End Date"
            icon={Calendar}
            minDate={startDate}
            maxDate={todayStr}
          />

          {/* Search Button */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2 invisible">Search</label>
            <button
              onClick={handleSearch}
              disabled={!selectedAgentId || !startDate || !endDate || loading}
              className="w-full px-4 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {hasFetched && (
        <>
          {/* Action bar */}
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {totalSessions} call{totalSessions !== 1 ? 's' : ''} found
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Column visibility */}
              <ColumnVisibilityDropdown
                columns={allTableColumns}
                visible={visibleColumns}
                onToggle={toggleVisibleColumn}
                onShowAll={showAllColumns}
                onHideAll={hideAllColumns}
              />

              <button
                onClick={() => loadCallHistory(currentPage)}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              {/* Export Dropdown */}
              <div className="relative" ref={exportRef}>
                <button
                  onClick={() => setExportOpen(!exportOpen)}
                  disabled={sessionsList.length === 0}
                  className="px-4 py-2 bg-success hover:bg-success/90 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${exportOpen ? 'rotate-180' : ''}`} />
                </button>

                {exportOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-elegant z-50 overflow-hidden">
                    <div className="p-3 border-b border-border bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                          <FileSpreadsheet className="w-4 h-4 text-primary" />
                          Select Columns to Export
                        </span>
                        <button onClick={() => setExportOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={selectAllExportCols} className="text-xs text-primary hover:underline">Select All</button>
                        <span className="text-xs text-muted-foreground">|</span>
                        <button onClick={deselectAllExportCols} className="text-xs text-primary hover:underline">Deselect All</button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
                      <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Standard Columns</div>
                      {baseColumns.map((col) => (
                        <button
                          key={col.key}
                          type="button"
                          onClick={() => toggleExportColumn(col.key)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                            selectedExportColumns[col.key] ? 'bg-primary border-primary' : 'border-border bg-background'
                          }`}>{selectedExportColumns[col.key] && <Check className="w-3 h-3 text-white" />}</div>
                          <span className="text-sm text-foreground">{col.label}</span>
                        </button>
                      ))}
                      {extractionFields.length > 0 && (
                        <>
                          <div className="px-2 py-1 mt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Extracted Data</div>
                          {extractionFields.map((f) => (
                            <button
                              key={`ext_${f.name}`}
                              type="button"
                              onClick={() => toggleExportColumn(`ext_${f.name}`)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer transition-colors text-left"
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                                selectedExportColumns[`ext_${f.name}`] ? 'bg-primary border-primary' : 'border-border bg-background'
                              }`}>{selectedExportColumns[`ext_${f.name}`] && <Check className="w-3 h-3 text-white" />}</div>
                              <span className="text-sm text-foreground">{f.name}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{f.data_type}</span>
                            </button>
                          ))}
                        </>
                      )}
                    </div>

                    <div className="p-3 border-t border-border">
                      <button
                        onClick={exportToCSV}
                        className="w-full px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Export {Object.values(selectedExportColumns).filter(Boolean).length} Columns
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" text="Loading call history..." />
            </div>
          ) : sessionsList.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Phone className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No calls found for the selected agent and date range.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full" style={{ minWidth: `${Math.max(600, (visibleBaseColumns.length + visibleExtractionFields.length) * 130)}px` }}>
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-border bg-muted/30 dark:bg-muted/50">
                      {visibleBaseColumns.map((col) => (
                        <th key={col.key} className={`${col.key === 'cost_usd' ? 'text-right' : 'text-left'} py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap`}>
                          {col.label}
                        </th>
                      ))}
                      {visibleExtractionFields.map((field) => (
                        <th key={field.name} className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap" title={field.description}>
                          {field.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {sessionsList.map((session) => (
                      <tr key={session.id} className="border-b border-border/30 hover:bg-muted/20 dark:hover:bg-muted/30 transition-all duration-150 group">
                        {visibleBaseColumns.map((col) => (
                          <td key={col.key} className={`py-4 px-3 ${col.key === 'cost_usd' ? 'text-right' : ''}`}>
                            {renderCell(session, col.key)}
                          </td>
                        ))}
                        {visibleExtractionFields.map((field) => {
                          const value = session.extracted_data?.[field.name];
                          const display = value != null && value !== '' ? String(value) : '-';
                          return (
                            <td key={field.name} className="py-4 px-3">
                              <span className={`text-sm ${display === '-' ? 'text-muted-foreground' : 'text-foreground'} font-medium truncate block max-w-[200px]`} title={display}>
                                {display}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-muted-foreground order-2 sm:order-1">
                Showing {((currentPage - 1) * sessionsPerPage) + 1} to {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} calls
              </div>
              <div className="flex space-x-2 order-1 sm:order-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
                >
                  Previous
                </button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        currentPage === pageNum ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted text-foreground'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CallHistoryExport;
